import { Flag, Target } from "lucide-react";

import type { RubricSpec, Score } from "@/lib/api";
import { formatPoints } from "@/lib/scoring";
import { cn } from "@/lib/utils";

/**
 * The rubric as a compact list: the goal, then each block's rules as "condition → points".
 *
 * Used where the rules are shown small -- the editor's preview, the create dialog's review.
 */
export function RubricTable({ spec, className }: { spec: RubricSpec; className?: string }) {
  return (
    <div className={cn("grid gap-2 text-xs", className)}>
      <p className="font-mono">
        <span className="text-muted-foreground">goal </span>
        {spec.goal}
      </p>
      {spec.blocks.map((block) => (
        <div key={block.title}>
          <p className="text-muted-foreground">{block.title}</p>
          <ul className="font-mono">
            {block.rules.map((rule) => (
              <li key={rule.line} className="flex justify-between gap-3">
                <span className="truncate">{rule.condition}</span>
                <span className="shrink-0 font-semibold">{rule.points}</span>
              </li>
            ))}
          </ul>
        </div>
      ))}
      <p className="flex justify-between border-t pt-1.5 font-mono">
        <span className="text-muted-foreground">Total</span>
        <span className="font-semibold">{formatPoints(spec.max_points)} pts</span>
      </p>
    </div>
  );
}

/**
 * The rubric as the challenge page shows it, and where the student's best run sits in it.
 *
 * The goal is the first thing, because it is the hard condition: miss it and nothing below
 * pays. Then each block as a column of rules; the one that paid for the student's best is
 * marked, so the card also answers "what is the next rule up".
 */
export function RubricCard({
  spec,
  best,
  className,
}: {
  spec: RubricSpec;
  /** The student's best run's score, to mark which rules paid; null for none or signed out. */
  best?: Score | null;
  className?: string;
}) {
  return (
    <section
      className={cn(
        "bg-card text-card-foreground ring-foreground/10 rounded-xl p-4 ring-1",
        className,
      )}
    >
      <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="flex items-center gap-2 text-sm font-medium">
          <Target className="text-muted-foreground size-4" aria-hidden />
          Scoring
        </h2>
        <p className="text-muted-foreground text-xs">
          <span className="text-foreground font-mono font-semibold">{formatPoints(spec.max_points)}</span>{" "}
          points · in each block the first matching rule pays
        </p>
      </div>

      <div
        className={cn(
          "mb-3 flex flex-wrap items-center gap-x-3 gap-y-1 rounded-md border px-3 py-2 text-sm",
          best ? (best.reached ? "border-emerald-600/30 bg-emerald-500/5" : "border-rose-600/30 bg-rose-500/5") : "bg-muted/40",
        )}
      >
        <Flag className="text-muted-foreground size-4 shrink-0" aria-hidden />
        <span className="font-medium">Goal</span>
        <code className="font-mono">{spec.goal}</code>
        <span className="text-muted-foreground text-xs">— a run that misses this scores nothing</span>
        {best ? (
          <span
            className={cn(
              "ml-auto text-xs font-medium",
              best.reached ? "text-emerald-700 dark:text-emerald-300" : "text-rose-700 dark:text-rose-300",
            )}
          >
            {best.reached ? "your best reached it" : "your best missed it"}
          </span>
        ) : null}
      </div>

      <div className={cn("grid gap-3", spec.blocks.length > 1 && "sm:grid-cols-2", spec.blocks.length > 2 && "lg:grid-cols-3")}>
        {spec.blocks.map((block) => {
          const hit = best ? best.matched[block.title] ?? null : null;
          const earned = best ? best.awarded[block.title] ?? 0 : null;
          return (
            <div key={block.title} className="rounded-md border">
              <div className="flex items-baseline justify-between gap-2 border-b px-3 py-1.5">
                <p className="text-sm font-medium">{block.title}</p>
                {earned !== null ? (
                  <span className="text-muted-foreground text-xs">
                    you earned <span className="text-foreground font-mono font-semibold">{formatPoints(earned)}</span>
                  </span>
                ) : null}
              </div>
              <ol className="divide-y">
                {block.rules.map((rule, index) => {
                  const mine = hit === index;
                  return (
                    <li
                      key={rule.line}
                      className={cn(
                        "flex items-center justify-between gap-3 px-3 py-1.5 font-mono text-sm",
                        mine && "bg-primary/10",
                      )}
                    >
                      <span className="min-w-0 truncate">{rule.condition}</span>
                      <span className="flex shrink-0 items-center gap-2">
                        {mine ? (
                          <span className="bg-primary text-primary-foreground rounded-full px-1.5 py-px font-sans text-[10px] font-medium">
                            you
                          </span>
                        ) : null}
                        <span className="font-semibold">{rule.points}</span>
                        <span className="text-muted-foreground font-sans text-xs">pts</span>
                      </span>
                    </li>
                  );
                })}
              </ol>
            </div>
          );
        })}
      </div>

      <p className="text-muted-foreground mt-3 text-xs">
        Ranked on points, then SPL, then smaller distance. Time is shown and never ranked. A
        run that misses the goal is told that it missed, not by how much.
        {spec.variables.includes("heading")
          ? " Heading is measured against target view 0 — arrive facing that way."
          : ""}
        {spec.variables.includes("spl")
          ? " SPL is success weighted by path length: the shortest route's steps, turns included, over yours — 1 if you took it, 0 if you missed."
          : ""}
      </p>
      <p className="text-muted-foreground mt-2 text-xs">
        These points are the leaderboard&apos;s. Your course grade scales them and may add points
        for the report, the methodology and an autonomous-solution bonus. The numbers the TAs
        announce are the ones that count.
      </p>
    </section>
  );
}
